/**
 * Mutex per interazioni MSAL redirect (loginRedirect/acquireTokenRedirect).
 *
 * NOTA: loginRedirect/acquireTokenRedirect causano page unload quasi istantaneamente.
 * Il lock NON viene rilasciato via releaseInteractionLock() — il TTL 30s è la
 * strategia di cleanup intenzionale. Non è un bug, è il design.
 *
 * Kill-switch: localStorage['focus.auth.v2.interactionLock'] = '0' per disattivare
 */

const LOCK_KEY = 'focus.auth.interactionLock';
const LOCK_TTL_MS = 30_000; // 30 secondi
const KILL_SWITCH_KEY = 'focus.auth.v2.interactionLock';

export type LockOwner =
  | 'loginPage'
  | 'scheduleRefresh'
  | 'acquireScheduleToken'
  | 'refreshTokenPDV';

interface LockData {
  owner: LockOwner;
  ts: number;
  reason: string;
  tabId: string;
}

let inMemoryLocked = false;

function readLockFromStorage(): LockData | null {
  try {
    const raw = localStorage.getItem(LOCK_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      'owner' in parsed &&
      'ts' in parsed
    ) {
      return parsed as LockData;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Tenta di acquisire il lock per un'interazione MSAL redirect.
 * Se il kill-switch è disattivato, ritorna sempre true (bypass).
 */
export function tryAcquireInteractionLock(
  owner: LockOwner,
  reason: string,
): boolean {
  // Kill-switch: se disattivato, permetti sempre
  if (localStorage.getItem(KILL_SWITCH_KEY) === '0') return true;

  // Se già locked in-memory, rifiuta
  if (inMemoryLocked) {
    console.warn(
      `[AuthInteractionLock] Lock rifiutato per ${owner}: già locked in-memory`,
    );
    return false;
  }

  // Controlla localStorage per lock cross-tab
  const existing = readLockFromStorage();
  if (existing && Date.now() - existing.ts < LOCK_TTL_MS) {
    console.warn(
      `[AuthInteractionLock] Lock rifiutato per ${owner}: lock attivo da ${existing.owner} (${Date.now() - existing.ts}ms fa)`,
    );
    return false;
  }

  // Acquisisci il lock
  inMemoryLocked = true;
  const tabId = localStorage.getItem('tabId') || '';
  const lockData: LockData = { owner, ts: Date.now(), reason, tabId };
  localStorage.setItem(LOCK_KEY, JSON.stringify(lockData));
  console.log(`[AuthInteractionLock] Lock acquisito da ${owner}: ${reason}`);
  return true;
}

/**
 * Rilascia il lock per un owner specifico.
 * NOTA: in pratica quasi mai chiamato — loginRedirect/acquireTokenRedirect
 * causano page unload. Il TTL è la strategia di cleanup primaria.
 */
export function releaseInteractionLock(owner: LockOwner): void {
  const existing = readLockFromStorage();
  if (existing && existing.owner === owner) {
    localStorage.removeItem(LOCK_KEY);
  }
  inMemoryLocked = false;
}

/**
 * Verifica se un lock è attivo (in-memory o in localStorage con TTL valido).
 */
export function isInteractionLocked(): boolean {
  if (inMemoryLocked) return true;
  const existing = readLockFromStorage();
  return existing !== null && Date.now() - existing.ts < LOCK_TTL_MS;
}

/**
 * Rimuove lock orfani al bootstrap dell'applicazione.
 * Chiamato PRIMA di setIsInitialized(true) in LoginMSALProvider.
 *
 * Un lock è orfano se:
 * - È della stessa tab (dopo page load, la pagina che lo ha acquisito non esiste più)
 * - Ha superato il TTL (30s) — safety net per lock cross-tab dimenticati
 */
export function cleanupOrphanInteractionLock(): void {
  const existing = readLockFromStorage();
  if (existing) {
    const myTabId = localStorage.getItem('tabId');
    const isOwnOrphan = existing.tabId === myTabId;
    const isTTLExpired = Date.now() - existing.ts >= LOCK_TTL_MS;

    if (isOwnOrphan || isTTLExpired) {
      console.log(
        `[AuthInteractionLock] Lock orfano rimosso (owner: ${existing.owner}, età: ${Date.now() - existing.ts}ms, motivo: ${isOwnOrphan ? 'stessa tab' : 'TTL scaduto'})`,
      );
      localStorage.removeItem(LOCK_KEY);
    }
  }
  inMemoryLocked = false;
}
